import { useState, useCallback} from "react";
import axios from "axios";
import { useUserContext } from "../../context/UserContext.tsx";
 
//const remoteUrl = "http://arthurst7-11.myliveeye.com:8000";
 
export interface TimelineTransaction {
  _id: string;
  posId: string;
  startedAt: string; // ISO date string
  date: string;      // ISO date string (end time)  
  [key: string]: any;
}
 
export function useTimelineTransactions() {
  const [timelineTransactions, setTimelineTransactions] = useState<TimelineTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [activeTransaction, setActiveTransaction] = useState<TimelineTransaction | null>(null);
  const { activeRemoteUrl } = useUserContext();
 
  /**
   * Fetch transactions in a time window (startTime → endTime)
   */
  const fetchTimelineTransactions = useCallback(
    async ({ siteId, startTime, endTime }: { siteId: string; startTime: number; endTime: number }) => {
      try {
        setLoadingTransactions(true);
 
        // const url = `${remoteUrl}/v1/pos/transactions/camera/${siteId}?startTime=${startTime}&endTime=${endTime}`;
        const url = `${activeRemoteUrl}/v1/pos/transactions/camera/${siteId}?startTime=${startTime}&endTime=${endTime}`
        const res = await axios.get(url);
 
        if (res.status === 200 && res?.data?.success) {
          const tx = res?.data?.success?.transactions ?? [];
          //console.log("[TimelineTx] ✅ fetched", tx.length, "transactions");
          setTimelineTransactions(tx);
        } else {
          console.warn("[TimelineTx] ❌ no success field, resetting");
          setTimelineTransactions([]);
        }
      } catch (err) {
        console.error("[TimelineTx] Failed to fetch transactions", err);
        setTimelineTransactions([]);
      } finally {
        setLoadingTransactions(false);
      }
    },
    []
  );
 
  /**
   * Reset timeline transactions
   */
  const resetTimelineTransactions = useCallback(() => {
    setTimelineTransactions([]);
  }, []);
 
  /**
   * Get the active transaction (covers currentTimestamp),
   * or the next upcoming transaction if playback moved past one.
   */
  const getActiveTransaction = useCallback(
    (currentTimestamp: number | null): TimelineTransaction | null => {
      if (!currentTimestamp || !timelineTransactions.length) return null;
 
      // find if any transaction covers current time
      const active = timelineTransactions.find((tx) => {
        const start = new Date(tx.startedAt).getTime();
        const end = new Date(tx.date).getTime();
        return currentTimestamp >= start && currentTimestamp <= end;
      });
 
      if (active) {
        console.log(
          "[TimelineTx] ▶ Active transaction:",
          active._id,
          "POS:", active.posId,
          "at", new Date(currentTimestamp).toLocaleString()
        );
        return active;
      }
 
      // else, find the very next transaction after current time
      const upcoming = timelineTransactions.find(
        (tx) => new Date(tx.startedAt).getTime() > currentTimestamp
      );
      if (upcoming) {
        console.log(
          "[TimelineTx] ⏩ Switching to next transaction:",
          upcoming._id,
          "POS:", upcoming.posId,
          "starting at", new Date(upcoming.startedAt).toLocaleString()
        );
      }
 
      return upcoming ?? null; 
    },
    [timelineTransactions]
  );
 
  /**
   * Returns a map of the latest transaction per POS at current time
   * (useful for overlay rendering like in playback).
   */
  const getCurrentPOSTransactions = useCallback(
    (currentTimestamp: number | null): Record<string, TimelineTransaction> => {
      if (!currentTimestamp || !timelineTransactions.length) return {};
 
      return timelineTransactions.reduce((acc, tx) => {
        const start = new Date(tx.startedAt).getTime();
        if (start > currentTimestamp) return acc; // ignore future
 
        if (!acc[tx.posId]) {
          acc[tx.posId] = tx;
        } else {
          const last = new Date(acc[tx.posId].startedAt).getTime();
          if (start > last) acc[tx.posId] = tx;
        }
        return acc;
      }, {} as Record<string, TimelineTransaction>);
    },
    [timelineTransactions]
  );
 
  const setActiveTransactionByTimestamp = useCallback(
  (timestamp: number | null) => {
    if (!timestamp) return;
 
    const tx = getActiveTransaction(timestamp);
    if (tx) {
      setActiveTransaction(tx);
    } else {
      setActiveTransaction(null);
    }
  },
  [getActiveTransaction]
);
 
  return {
    timelineTransactions,
    loadingTransactions,
    fetchTimelineTransactions,
    resetTimelineTransactions,
    getActiveTransaction,
    getCurrentPOSTransactions,
    setActiveTransactionByTimestamp
  };
}
 
